import { msg } from '@lingui/core/macro';
import { EmissionsModelReleaseLifecyclePhase as Phase } from '@watershed/constants/emissionsModel';
import {
  assertNoTypeDifferences,
  noTypeDifferences,
} from '@watershed/shared-util/src/assertNoTypeDifferences';
import { MessageDescriptor } from '@lingui/core';
import { i18n } from '@watershed/intl';

/** Lifecycle phases in progressive order */
const linearPhaseOrder = [
  Phase.Alpha,
  Phase.Beta,
  Phase.Current,
  Phase.LTS,
  Phase.Maintenance,
  Phase.EndOfLife,
];
// assert that all of the enum values are present in the order array
assertNoTypeDifferences<Phase, (typeof linearPhaseOrder)[number]>(
  noTypeDifferences
);

/**
 * When we show phases to the user in a dropdown, we sort them in linear progression order
 * so that Alpha/Beta (experimental) versions appear at the top:
 * Alpha → Beta → Current → LTS → Maintenance → EndOfLife.
 */
const userVisibleSortPhaseOrder = [
  Phase.Alpha,
  Phase.Beta,
  Phase.Current,
  Phase.LTS,
  Phase.Maintenance,
  Phase.EndOfLife,
];

// assert that all of the enum values are present in the user visible sort order array
assertNoTypeDifferences<Phase, (typeof userVisibleSortPhaseOrder)[number]>(
  noTypeDifferences
);

/**
 * When picking the default CMV, we still want Current first (the stable/recommended version),
 * then Beta/Alpha, then LTS/the rest. This preserves the old default selection behavior.
 *
 * Also used when determining the "best" lifecycle phase for a customization.
 */
export const defaultSelectionPhaseOrder: Array<Phase> = [
  Phase.Current,
  Phase.Beta,
  Phase.Alpha,
  Phase.LTS,
  Phase.Maintenance,
  Phase.EndOfLife,
];

// assert that all of the enum values are present in the default selection order array
assertNoTypeDifferences<Phase, (typeof defaultSelectionPhaseOrder)[number]>(
  noTypeDifferences
);

const nextPhaseOptions: Record<Phase, Array<Phase>> = {
  [Phase.Alpha]: [Phase.Beta, Phase.EndOfLife],
  [Phase.Beta]: [Phase.Current, Phase.EndOfLife],
  [Phase.Current]: [Phase.LTS, Phase.Maintenance, Phase.EndOfLife],
  [Phase.LTS]: [Phase.Maintenance, Phase.EndOfLife],
  [Phase.Maintenance]: [Phase.EndOfLife],
  [Phase.EndOfLife]: [],
};

const phaseColor: Record<Phase, 'primary' | 'secondary' | 'warning' | 'error'> =
  {
    [Phase.Alpha]: 'secondary',
    [Phase.Beta]: 'warning',
    [Phase.Current]: 'primary',
    [Phase.LTS]: 'warning',
    [Phase.Maintenance]: 'warning',
    [Phase.EndOfLife]: 'error',
  };

const displayName: Record<Phase, MessageDescriptor> = {
  [Phase.Alpha]: msg({
    message: 'Alpha',
    context: 'Methodology lifecycle phase',
  }),
  [Phase.Beta]: msg({
    message: 'Beta',
    context: 'Methodology lifecycle phase',
  }),
  [Phase.Current]: msg({
    message: 'Current',
    context: 'Methodology lifecycle phase',
  }),
  [Phase.LTS]: msg({
    message: 'Outdated',
    context: 'Methodology lifecycle phase',
  }),
  [Phase.Maintenance]: msg({
    message: 'Maintenance',
    context: 'Methodology lifecycle phase',
  }),
  [Phase.EndOfLife]: msg({
    message: 'Unusable',
    context: 'Methodology lifecycle phase',
  }),
};

const description: Record<Phase, MessageDescriptor> = {
  [Phase.Alpha]: msg`This is an alpha version and is not ready to be used in real footprints.`,
  [Phase.Beta]: msg`This is a beta version. It may contain bugs, and is not ready for verification.`,
  [Phase.Current]: msg`This is the version that's ready for verification.`,
  [Phase.LTS]: msg`A newer version of this calculation method is available.`,
  [Phase.Maintenance]: msg`This version is in maintenance. It can no longer be used in footprints unless you have used it previously.`,
  [Phase.EndOfLife]: msg`This version can no longer be used in footprints.`,
};

export type PhasePermission =
  | 'addOrgAccess'
  | 'convertGlobalToOrgGated'
  | 'convertOrgGatedToGlobal'
  | 'editMetadata'
  | 'bulkOperations'
  | 'editEmissionsModels'
  | 'editGlobalFootprintTags'
  | 'deleteCalculationMethodVersion'
  | 'addMeasurementTest'
  | 'addLinkagesWithoutReview';
const permissions: Record<Phase, Record<PhasePermission, boolean>> = {
  [Phase.Alpha]: {
    addOrgAccess: true,
    convertGlobalToOrgGated: true,
    convertOrgGatedToGlobal: true,
    editMetadata: true,
    bulkOperations: true,
    editEmissionsModels: true,
    editGlobalFootprintTags: true,
    deleteCalculationMethodVersion: true,
    addMeasurementTest: true,
    addLinkagesWithoutReview: true,
  },
  [Phase.Beta]: {
    addOrgAccess: true,
    convertGlobalToOrgGated: true,
    convertOrgGatedToGlobal: true,
    editMetadata: true,
    bulkOperations: true,
    editEmissionsModels: true,
    editGlobalFootprintTags: true,
    deleteCalculationMethodVersion: true,
    addMeasurementTest: true,
    addLinkagesWithoutReview: false,
  },
  [Phase.Current]: {
    addOrgAccess: true,
    convertGlobalToOrgGated: false,
    convertOrgGatedToGlobal: true,
    editMetadata: true,
    bulkOperations: false,
    editEmissionsModels: false,
    // NOTE: true for now as our EMs are too intertwined
    // to lock in default tags for current releases. Can
    // consider making this false eventually
    // https://watershedclimate.slack.com/archives/C030LTRESSG/p1715189076008599?thread_ts=1715185748.543769&cid=C030LTRESSG
    editGlobalFootprintTags: true,
    deleteCalculationMethodVersion: false,
    addMeasurementTest: true,
    addLinkagesWithoutReview: false,
  },
  [Phase.LTS]: {
    addOrgAccess: false,
    convertGlobalToOrgGated: false,
    convertOrgGatedToGlobal: false,
    editMetadata: false,
    bulkOperations: false,
    editEmissionsModels: false,
    editGlobalFootprintTags: false,
    deleteCalculationMethodVersion: false,
    addMeasurementTest: false,
    addLinkagesWithoutReview: false,
  },
  [Phase.Maintenance]: {
    addOrgAccess: false,
    convertGlobalToOrgGated: false,
    convertOrgGatedToGlobal: false,
    editMetadata: false,
    bulkOperations: false,
    editEmissionsModels: false,
    editGlobalFootprintTags: false,
    deleteCalculationMethodVersion: false,
    addMeasurementTest: false,
    addLinkagesWithoutReview: false,
  },
  [Phase.EndOfLife]: {
    addOrgAccess: false,
    convertGlobalToOrgGated: false,
    convertOrgGatedToGlobal: false,
    editMetadata: false,
    bulkOperations: false,
    editEmissionsModels: false,
    editGlobalFootprintTags: false,
    deleteCalculationMethodVersion: true,
    addMeasurementTest: false,
    addLinkagesWithoutReview: false,
  },
};

export class LifecyclePhase {
  static initialPhase = Phase.Alpha;
  static betaPhase = Phase.Beta;
  static defaultPhase = Phase.Current;
  static terminalPhase = Phase.EndOfLife;
  static get allPhases(): Array<string> {
    return Object.values(Phase);
  }

  private phase: Phase;
  constructor(phase: Phase) {
    this.phase = phase;
  }

  get displayName(): string {
    return i18n._(displayName[this.phase]);
  }

  get description(): string {
    return i18n._(description[this.phase]);
  }

  get color(): (typeof phaseColor)[Phase] {
    return phaseColor[this.phase];
  }

  get nextPhase(): Phase | null {
    return nextPhaseOptions[this.phase][0] ?? null;
  }

  get nextPhaseOptions(): Array<Phase> {
    return nextPhaseOptions[this.phase];
  }

  get isInitial(): boolean {
    return this.phase === LifecyclePhase.initialPhase;
  }

  get isDefault(): boolean {
    return this.phase === LifecyclePhase.defaultPhase;
  }

  get isTerminal(): boolean {
    return this.phase === LifecyclePhase.terminalPhase;
  }

  get isDeprecated(): boolean {
    return this.phase === Phase.Maintenance || this.phase === Phase.EndOfLife;
  }

  isBefore(other: Phase): boolean {
    return (
      linearPhaseOrder.indexOf(this.phase) < linearPhaseOrder.indexOf(other)
    );
  }

  isAfter(other: Phase): boolean {
    return (
      linearPhaseOrder.indexOf(this.phase) > linearPhaseOrder.indexOf(other)
    );
  }

  /**
   * When we show phases to the user in a dropdown, we sort them in linear progression order
   * so that Alpha/Beta (experimental) versions appear at the top:
   * Alpha → Beta → Current → LTS → Maintenance → EndOfLife.
   */
  userVisibleSort(other: Phase): number {
    return (
      userVisibleSortPhaseOrder.indexOf(this.phase) -
      userVisibleSortPhaseOrder.indexOf(other)
    );
  }

  /**
   * When picking the default CMV, we still want Current first (the stable/recommended version),
   * then Beta/Alpha, then LTS/the rest.
   */
  defaultSelectionSort(other: Phase): number {
    return (
      defaultSelectionPhaseOrder.indexOf(this.phase) -
      defaultSelectionPhaseOrder.indexOf(other)
    );
  }

  hasPermission(permission: PhasePermission): boolean {
    return permissions[this.phase][permission];
  }
}

export function phaseDetails(phase: Phase): LifecyclePhase {
  return new LifecyclePhase(phase);
}

/**
 * Selects the most favorable lifecycle phase from a list of phases.
 *
 * Uses `defaultSelectionPhaseOrder` to determine preference order (Current > Beta >
 * Alpha > LTS > Maintenance > EndOfLife), returning the first (most favorable) phase
 * found. This is used when deriving a customization's status from its compatible
 * calculation method versions - we show the best available status rather than the worst.
 *
 * @param phases - Array of lifecycle phases to choose from
 * @returns The best phase found, or `EndOfLife` if the array is empty or no phases match
 */
export function getBestLifecyclePhase(phases: Array<Phase>): Phase {
  for (const phase of defaultSelectionPhaseOrder) {
    if (phases.includes(phase)) {
      return phase;
    }
  }
  return Phase.EndOfLife;
}
